import {ellipsis} from 'ellipsed';

import slideDown from '../util/slide-down';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {register} from '../vendor/theme-scripts/theme-sections';
import Media from '../media/media';
import {productFormSection} from '../features/product-form';
import {swatchSection} from '../features/swatch';
import {tooltipSection} from '../features/tooltip';
import {popoutSection} from '../features/popout';
import accordions from '../features/accordion';
import QuantityCounter from '../globals/quantity-handle';
import copyClipboard from '../features/copy-clipboard';
import tabs from '../features/tabs';

window.theme.variables = {
  productPageSticky: false,
  bpSmall: false,
  mediaQuerySmall: 750,
};

const selectors = {
  addToCart: '[data-add-to-cart]',
  priceWrapper: '[data-price-wrapper]',
  slideshow: '[data-product-slideshow]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  form: '[data-product-form]',
  thumbs: '[data-product-thumbs]',
  dataSectionId: 'data-section-id',
  dataTallLayout: 'data-tall-layout',
  dataStickyEnabled: 'data-sticky-enabled',
  dataCartBar: 'data-cart-bar',
  dataReviews: 'data-reviews',
  dataProductShare: '[data-product-share]',
  productPage: '.product__page',
  formWrapper: '.form__wrapper',
  cartBar: '#cart-bar',
  productSubmitAdd: '.product__submit__add',
  cartBarAdd: 'data-add-to-cart-bar',
  cartBarScroll: 'data-cart-bar-scroll',
  templateProduct: '#template-product',
  siteFooterWrapper: '.site-footer-wrapper',
  shopifyProductReviews: '#shopify-product-reviews',
  toggleTruncateHolder: '[data-truncated-holder]',
  toggleTruncateButton: '[data-truncated-button]',
  toggleTruncateContent: '[data-truncated-content]',
  toggleTruncateContentAttr: 'data-truncated-content',
  headerSticky: '[data-header-sticky="sticky"]',
  upsellButton: '[data-upsell-btn]',
  upsellButtonText: '[data-upsell-btn-text]',
  scrollToElement: '[data-scroll-to]',
  accordionToggle: '[data-accordion-toggle]',
  accordionBody: '[data-accordion-body]',
  headerHeight: '[data-header-height]',
};

const classes = {
  classExpanded: 'is-expanded',
  classSticky: 'is-sticky',
  classStickyHeader: 'with-sticky-header',
  classVisible: 'is-visible',
  classSiteFooterPush: 'site-footer--push',
  open: 'is-open',
};

const sections = {};

/**
 * Product section constructor.
 * @param {string} container - selector for the section container DOM element
 */
class Product {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.id = this.container.getAttribute(selectors.dataSectionId);
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.stickyEnabled = this.container.getAttribute(selectors.dataStickyEnabled) === 'true';
    this.headerSticky = document.querySelector(selectors.headerSticky) !== null;
    this.showReviews = this.container.getAttribute(selectors.dataReviews) === 'true';
    this.thumbs = this.container.querySelector(selectors.thumbs);
    this.shareButton = this.container.querySelector(selectors.dataProductShare);
    this.upsellButton = this.container.querySelector(selectors.upsellButton);
    this.scrollToButton = this.container.querySelector(selectors.scrollToElement);
    this.truncateElementHolder = this.container.querySelector(selectors.toggleTruncateHolder);
    this.truncateElement = this.container.querySelector(selectors.toggleTruncateContent);
    this.resizeEventTruncate = () => this.truncateText();
    this.resizeEventSticky = () => this.stickyScrollCheck();
    this.resizeEventUpsell = () => this.calcUpsellButtonDemensions();
    this.scrollEvent = () => this.scrollTop();

    if (this.scrollToButton) {
      this.scrollToReviews();
    }

    this.shareToggle();

    if (this.truncateElementHolder && this.truncateElement) {
      setTimeout(this.resizeEventTruncate, 50);
      document.addEventListener('theme:resize', this.resizeEventTruncate);
    }

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    const productJson = this.container.querySelector(selectors.productJson);
    if ((productJson && !productJson.innerHTML) || !productJson) {
      const counter = new QuantityCounter(this.container);
      counter.init();
      return;
    }

    this.form = this.container.querySelector(selectors.form);

    this.init();

    if (this.stickyEnabled) {
      this.stickyScroll();
    }

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.initCartBar();
    }

    const reviewsAppInstalled = typeof window.SPR == 'function';

    if (this.showReviews && reviewsAppInstalled) {
      window.SPR.initDomEls();
      window.SPR.loadBadges();
    }

    if (this.upsellButton) {
      this.upsellButtonDemensions();
    }
  }

  init() {
    theme.mediaInstances[this.id] = new Media(this.section);
    theme.mediaInstances[this.id].init();
  }

  upsellButtonDemensions() {
    this.calcUpsellButtonDemensions();

    document.addEventListener('theme:resize', this.resizeEventUpsell);
  }

  calcUpsellButtonDemensions() {
    const upsellButtonText = this.upsellButton.querySelector(selectors.upsellButtonText);
    if (upsellButtonText) {
      this.upsellButton.style.setProperty('--btn-text-width', `${upsellButtonText.clientWidth}px`);
    }
  }

  stickyScroll() {
    this.stickyScrollCheck();

    document.addEventListener('theme:resize', this.resizeEventSticky);
  }

  stickyScrollCheck() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= theme.variables.mediaQuerySmall;
    const form = this.container.querySelector(selectors.formWrapper);
    const targetFormWrapper = this.container.querySelector(`${selectors.productPage} ${selectors.formWrapper}`);

    let noReviews = true;
    if (this.showReviews) {
      noReviews = false;
    }

    if (isDesktop) {
      const slideshow = this.container.querySelector(selectors.slideshow);
      if (!form || !slideshow) return;
      const productCopyHeight = form.offsetHeight;
      const productImagesHeight = slideshow.offsetHeight;

      // Is the product description and form taller than window space
      // Is is also shorter than the window and images
      if (productCopyHeight < productImagesHeight && productCopyHeight < window.innerHeight && noReviews) {
        theme.variables.productPageSticky = true;

        targetFormWrapper.classList.add(classes.classSticky);
        if (this.headerSticky) {
          targetFormWrapper.classList.add(classes.classStickyHeader);
        }
      } else {
        theme.variables.productPageSticky = false;
        targetFormWrapper.classList.remove(classes.classSticky);
      }
    } else {
      targetFormWrapper.classList.remove(classes.classSticky);
    }
  }

  truncateText() {
    const truncateRows = 5;
    const truncateElementCloned = this.truncateElement.cloneNode(true);
    const truncateElementClass = this.truncateElement.getAttribute(selectors.toggleTruncateContentAttr);
    const truncateNextElement = this.truncateElement.nextElementSibling;
    if (truncateNextElement) {
      truncateNextElement.remove();
    }

    this.truncateElement.parentElement.append(truncateElementCloned);

    const truncateAppendedElement = this.truncateElement.nextElementSibling;
    truncateAppendedElement.classList.add(truncateElementClass);
    truncateAppendedElement.removeAttribute(selectors.toggleTruncateContentAttr);

    showElement(truncateAppendedElement);

    ellipsis(truncateAppendedElement, truncateRows, {
      replaceStr: '',
    });

    hideElement(truncateAppendedElement);

    if (this.truncateElement.innerHTML !== truncateAppendedElement.innerHTML) {
      this.truncateElementHolder.classList.add(classes.classExpanded);
    } else {
      truncateAppendedElement.remove();
      this.truncateElementHolder.classList.remove(classes.classExpanded);
    }

    this.toggleTruncatedContent(this.truncateElementHolder);
  }

  toggleTruncatedContent(holder) {
    const toggleButton = holder.querySelector(selectors.toggleTruncateButton);
    if (toggleButton) {
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        holder.classList.remove(classes.classExpanded);
      });
    }
  }

  shareToggle() {
    if (this.shareButton) {
      this.shareButton.addEventListener('click', function () {
        if (navigator.share) {
          navigator
            .share({
              title: 'WebShare API Demo',
              url: 'https://codepen.io/ayoisaiah/pen/YbNazJ',
            })
            .then(() => {
              console.log('Thanks for sharing!');
            })
            .catch(console.error);
        } else {
          this.parentElement.classList.toggle(classes.classExpanded);
        }
      });
    }
  }

  initCartBar() {
    const cartBar = document.querySelector(selectors.cartBar);
    const cartBarBtn = cartBar.querySelector(selectors.productSubmitAdd);

    // Submit product form on cart bar button click
    if (cartBarBtn) {
      cartBarBtn.addEventListener('click', (e) => {
        e.preventDefault();

        if (e.target.hasAttribute(selectors.cartBarAdd)) {
          this.form.querySelector(selectors.addToCart).dispatchEvent(
            new Event('click', {
              bubbles: true,
            })
          );
        } else if (e.target.hasAttribute(selectors.cartBarScroll)) {
          this.scrollToTop();
        }
      });

      if (cartBarBtn.hasAttribute(selectors.cartBarAdd)) {
        document.addEventListener('product:bar:error', () => this.scrollToTop());
      }
    }

    this.cartBar = cartBar;

    document.addEventListener('theme:scroll', this.scrollEvent);
  }

  scrollToTop() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= theme.variables.mediaQuerySmall;
    const scrollTarget = isDesktop ? this.container : this.form;

    window.scrollTo({
      top: scrollTarget.getBoundingClientRect().top + window.scrollY,
      left: 0,
      behavior: 'smooth',
    });
  }

  scrollTop() {
    const scrolled = window.pageYOffset;
    const siteFooter = document.querySelector(selectors.siteFooterWrapper);

    if (this.form && this.cartBar) {
      const formOffset = this.form.offsetTop;
      const formHeight = this.form.offsetHeight;
      const checkPosition = scrolled > formOffset + formHeight;

      this.cartBar.classList.toggle(classes.classVisible, checkPosition);
      siteFooter.classList.toggle(classes.classSiteFooterPush, checkPosition);

      siteFooter.style.marginBottom = siteFooter.classList.contains(classes.classSiteFooterPush) ? `${this.cartBar.offsetHeight}px` : '0';
    }
  }

  scrollToReviews() {
    this.scrollToButton.addEventListener('click', (e) => {
      e.preventDefault();

      const element = document.querySelector(this.scrollToButton.getAttribute('href'));

      if (!element) {
        return;
      }

      let headerHeight = 0;

      if (this.headerSticky) {
        headerHeight = document.querySelector(selectors.headerHeight).getBoundingClientRect().height;
      }

      const accordionBody = element.querySelector(selectors.accordionBody);
      const accordionToggle = element.querySelector(selectors.accordionToggle);

      window.scrollTo({
        top: element.getBoundingClientRect().top - headerHeight,
        left: 0,
        behavior: 'smooth',
      });

      slideDown(accordionBody);
      accordionToggle.classList.add(classes.open);
    });

    // Adding attributes to Yotpo elements to be missed screen readers
    window.onload = () => {
      if (this.scrollToButton.nextElementSibling.querySelectorAll('*')) {
        this.scrollToButton.nextElementSibling.querySelectorAll('*').forEach((item) => {
          item.setAttribute('tabindex', '-1');
          item.setAttribute('aria-hidden', true);
        });
      }
    };
  }

  onUnload() {
    if (this.truncateElementHolder && this.truncateElement) {
      document.removeEventListener('theme:resize', this.resizeEventTruncate);
    }

    if (this.stickyEnabled) {
      document.removeEventListener('theme:resize', this.resizeEventSticky);
    }

    if (this.upsellButton) {
      document.removeEventListener('theme:resize', this.resizeEventUpsell);
    }

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      document.removeEventListener('theme:scroll', this.scrollEvent);
    }
  }
}

const productSection = {
  onLoad() {
    sections[this.id] = new Product(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

register('product', [productFormSection, productSection, swatchSection, tooltipSection, popoutSection, tabs, accordions, copyClipboard]);
